/**
 * @fileoverview Investigations section — Overview tab
 * @module jobs/phases/overview/investigations/InvestigationsSection
 *
 * Purpose: Stacks a compact, read-only snapshot of each investigation agent
 * (Books & Records, Solvency Analysis, Recoverable Transactions, Civil &
 * Criminal Offences) on the job Overview. Each snapshot deep-links to the
 * corresponding Review & Analysis workspace via its review slug.
 *
 * Used by: PlanPhase (Overview + Investigations tabs).
 */

import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { BooksRecordsSnapshot } from "./BooksRecordsSnapshot";
import { SolvencySnapshot } from "./SolvencySnapshot";
import { RecoverableTransactionsSnapshot } from "./RecoverableTransactionsSnapshot";
import { OffencesSnapshot } from "./OffencesSnapshot";
import { isReviewAgentVisibleOnJob } from "../../review/reviewRegistry";

interface InvestigationsSectionProps {
  jobId: Id<"jobs">;
}

/**
 * The four investigation snapshots, each linking to its Review & Analysis view.
 *
 * @component
 */
export function InvestigationsSection({ jobId }: InvestigationsSectionProps) {
  const navigate = useNavigate();
  const { data: job } = useQuery(
    convexQuery(api.jobs.queries.getJob, { jobId }),
  );
  const goToReview = (agent: string) =>
    navigate({
      to: "/dashboard/jobs/$jobId/review/$agent",
      params: { jobId, agent },
    });

  // Civil & Criminal Offences has no bankruptcy analogue yet, so it is hidden
  // on personal-insolvency jobs (mirrors the sidebar / review overview gating).
  // Wait for the job to resolve before deciding so a bankruptcy job never
  // flashes the card in.
  const showOffences =
    !!job &&
    isReviewAgentVisibleOnJob(
      "civil-criminal-offences",
      job.appointmentType ?? undefined,
    );

  return (
    <>
      <BooksRecordsSnapshot
        jobId={jobId}
        onSeeDetails={() => goToReview("books-and-records")}
      />
      <SolvencySnapshot
        jobId={jobId}
        onSeeDetails={() => goToReview("financial-analysis")}
      />
      <RecoverableTransactionsSnapshot
        jobId={jobId}
        onSeeDetails={() => goToReview("flagged-transactions")}
      />
      {showOffences && (
        <OffencesSnapshot
          jobId={jobId}
          onSeeDetails={() => goToReview("civil-criminal-offences")}
        />
      )}
    </>
  );
}
